import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { tournamentApi, eventApi, staffApi } from '../../services/api';
import { useConfirm } from '../../components/ConfirmDialog';
import { useTournamentData } from './hooks/useTournamentData';
import DetailsTab from './tabs/DetailsTab';
import PlayersTab from './tabs/PlayersTab';
import DrawTab from './tabs/DrawTab';
import ScheduleTab from './tabs/ScheduleTab';
import AnalyticsTab from './tabs/AnalyticsTab';
import EventsTab from './tabs/EventsTab';
import StaffTab from './tabs/StaffTab';
import ScoreModal from './modals/ScoreModal';
import ScheduleModal from './modals/ScheduleModal';

const TournamentDetail = () => {
  const { id } = useParams();
  const { user, isAuthenticated } = useAuth();
  const confirm = useConfirm();

  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  // Modal visibility
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [reopenRemarks, setReopenRemarks] = useState('');
  const [reopening, setReopening] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [registrationHistory, setRegistrationHistory] = useState([]);
  const [showDeletedScheduleModal, setShowDeletedScheduleModal] = useState(false);
  const [deletedSchedule, setDeletedSchedule] = useState([]);
  const [showDeleteScheduleConfirm, setShowDeleteScheduleConfirm] = useState(false);
  const [deletingSchedule, setDeletingSchedule] = useState(false);
  const [clearScheduleEvents, setClearScheduleEvents] = useState([]);
  const [clearScheduleEventIds, setClearScheduleEventIds] = useState(null); // null = all
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [staffPerms, setStaffPerms] = useState(null);
  const [scheduleEventFilter, setScheduleEventFilter] = useState('all');

  const { tournament, players, matches, loading, error, reload } = useTournamentData(id, setToast);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const isOwner = !!(user && (user.role === 'admin' || (tournament && tournament.created_by === user.id)));
  const isUmpire = !!(user && user.role === 'umpire');
  // canScore: any staff (including umpires) can enter/live-score matches
  const canScore = isOwner || !!(staffPerms && staffPerms.is_staff);
  // isOrganizer: staff with management rights — excludes umpires
  const isOrganizer = (isOwner || !!(staffPerms && staffPerms.is_staff)) && !isUmpire;

  // Fetch this user's permissions for the tournament (staff check)
  useEffect(() => {
    if (!isAuthenticated || !tournament?.id) return;
    staffApi.getMyPermissions(tournament.id)
      .then(res => setStaffPerms(res.data))
      .catch(() => {});
  }, [isAuthenticated, tournament?.id]);

  useEffect(() => {
    if (activeTab === 'analytics' && isOrganizer && tournament && !analytics) {
      setAnalyticsLoading(true);
      tournamentApi.getAnalytics(tournament.id)
        .then(res => setAnalytics(res.data))
        .catch(() => {})
        .finally(() => setAnalyticsLoading(false));
    }
  }, [activeTab, isOrganizer, tournament, analytics]);
  const isRegistered = players.some(p => p.user_id === user?.id);


  const getRoundName = (roundNumber, eventId = undefined) => {
    if (tournament?.draw_type === 'league') return `Round ${roundNumber}`;
    // Filter per event when event_id is known, so multi-event tournaments
    // don't add up match counts across events (causing "Round of 160" etc.)
    const matchesInRound = matches.filter(m =>
      m.round_number === roundNumber &&
      m.notes !== '3rd Place Playoff' &&
      (eventId !== undefined ? m.event_id === eventId : true)
    ).length;
    if (matchesInRound === 1) return 'Final';
    if (matchesInRound === 2) return 'Semi Finals';
    if (matchesInRound === 4) return 'Quarter Finals';
    if (matchesInRound === 8) return 'Round of 16';
    if (matchesInRound === 16) return 'Round of 32';
    return `Round of ${matchesInRound * 2}`;
  };

  const handleRegister = async () => {
    try { await tournamentApi.registerPlayer(id, { user_id: user.id }); reload(); }
    catch (err) { alert(err.response?.data?.error || 'Failed to register'); }
  };

  const handleCloseRegistration = async () => {
    try { await tournamentApi.update(id, { status: 'seeding' }); reload(); alert('Registration closed!'); }
    catch (err) { alert(err.response?.data?.error || 'Failed'); }
  };

  const handleUpdateMatch = async (matchId, winnerId) => {
    try { await tournamentApi.updateMatch(matchId, { winner_id: winnerId, status: 'completed' }); reload(); }
    catch (err) { alert(err.response?.data?.error || 'Failed'); }
  };

  const handleOpenScoreModal = (match) => {
    setSelectedMatch(match);
    setShowScoreModal(true);
  };

  const handleReopenRegistration = async () => {
    if (!reopenRemarks.trim()) { alert('Remarks required'); return; }
    setReopening(true);
    try { await tournamentApi.reopenRegistration(id, reopenRemarks); setShowReopenModal(false); setReopenRemarks(''); reload(); alert('Reopened!'); }
    catch (err) { alert(err.response?.data?.error || 'Failed'); }
    finally { setReopening(false); }
  };

  const handleLoadHistory = async () => {
    try { const response = await tournamentApi.getRegistrationHistory(id); setRegistrationHistory(response.data); setShowHistoryModal(true); }
    catch (err) { alert(err.response?.data?.error || 'Failed'); }
  };

  const handleDeleteSchedule = () => {
    setShowDeleteScheduleConfirm(true);
    setClearScheduleEventIds(null);
    eventApi.list(id)
      .then(res => setClearScheduleEvents(res.data || []))
      .catch(() => setClearScheduleEvents([]));
  };

  const confirmDeleteSchedule = async () => {
    setDeletingSchedule(true);
    try {
      const payload = clearScheduleEventIds && clearScheduleEventIds.length > 0
        ? { event_ids: clearScheduleEventIds }
        : undefined;
      await tournamentApi.deleteSchedule(id, payload);
      reload();
      setShowDeleteScheduleConfirm(false);
      alert('Schedule timings cleared successfully!');
    } catch (err) { alert(err.response?.data?.error || err.message || 'Failed to delete schedule'); }
    finally { setDeletingSchedule(false); }
  };

  const handleViewDeletedSchedule = async () => {
    try { const response = await tournamentApi.getDeletedSchedule(id); setDeletedSchedule(response.data); setShowDeletedScheduleModal(true); }
    catch (err) { alert(err.response?.data?.error || 'Failed to load deleted schedule'); }
  };

  const handlePermanentDeleteSchedule = async () => {
    if (!await confirm({ title: 'Permanently Delete Schedule', message: 'This action cannot be undone. All deleted schedule records will be removed.', confirmLabel: 'Delete Permanently', danger: true })) return;
    setDeletingSchedule(true);
    try { await tournamentApi.permanentDeleteSchedule(id); setShowDeletedScheduleModal(false); reload(); setToast('Schedule permanently deleted!'); }
    catch (err) { setToast(err.response?.data?.error || 'Failed to permanently delete schedule'); }
    finally { setDeletingSchedule(false); }
  };

  const STATUS_MAP = {
    draft:        { bg: '#f3f4f6', color: '#6b7280',  label: 'Draft' },
    registration: { bg: '#dbeafe', color: '#1d4ed8',  label: 'Registration Open' },
    seeding:      { bg: '#fef3c7', color: '#b45309',  label: 'Seeding' },
    in_progress:  { bg: '#d1fae5', color: '#065f46',  label: 'In Progress' },
    completed:    { bg: '#d1fae5', color: '#065f46',  label: 'Completed' },
  };

  if (loading) return <div className="container"><div className="loading"><div className="spinner"></div></div></div>;
  if (error || !tournament) return (
    <div className="container">
      <div className="alert alert-error">{error || 'Tournament not found'}</div>
      <Link to="/tournaments" className="btn btn-primary">Back</Link>
    </div>
  );

  const statusStyle = STATUS_MAP[tournament.status] || { bg: '#f3f4f6', color: '#6b7280', label: tournament.status };

  return (
    <div className="container">
      <div className="page-header">
        <div>
          <Link to="/tournaments" style={{ fontSize: '0.8rem', color: '#9ca3af', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            ← Tournaments
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.4rem', flexWrap: 'wrap' }}>
            <h1 className="page-title" style={{ margin: 0 }}>{tournament.name}</h1>
            <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600, background: statusStyle.bg, color: statusStyle.color, whiteSpace: 'nowrap' }}>
              {statusStyle.label}
            </span>
          </div>
          <a
            href={`/t/${id}`}
            target="_blank"
            rel="noreferrer"
            style={{ fontSize: '0.78rem', color: '#9ca3af', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}
          >
            Public view ↗
          </a>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {isOrganizer && tournament.status === 'registration' && (
            <button onClick={handleCloseRegistration} className="btn btn-warning">Close Registration</button>
          )}
          {isOrganizer && tournament.status === 'seeding' && (
            <>
              <button onClick={() => setShowReopenModal(true)} className="btn btn-outline">Reopen Registration</button>
              <button onClick={() => setActiveTab('draw')} className="btn btn-success">
                {tournament.draw_type === 'league' ? 'Generate Fixtures' : 'Generate Draw'}
              </button>
            </>
          )}
          {isOrganizer && tournament.status === 'in_progress' && (
            <>
              <button onClick={() => setShowReopenModal(true)} className="btn btn-outline">Reopen Registration</button>
              <button onClick={handleLoadHistory} className="btn btn-outline">View History</button>
            </>
          )}
        </div>
      </div>

      <div className="tabs">
        <button className={`tab ${activeTab === 'details' ? 'active' : ''}`} onClick={() => setActiveTab('details')}>Details</button>
        <button className={`tab ${activeTab === 'events' ? 'active' : ''}`} onClick={() => setActiveTab('events')}>Events</button>
        <button className={`tab ${activeTab === 'players' ? 'active' : ''}`} onClick={() => setActiveTab('players')}>Players ({players.length})</button>
        <button className={`tab ${activeTab === 'draw' ? 'active' : ''}`} onClick={() => setActiveTab('draw')}>Draw</button>
        <button className={`tab ${activeTab === 'schedule' ? 'active' : ''}`} onClick={() => setActiveTab('schedule')}>Schedule</button>
        {isOrganizer && (
          <button className={`tab ${activeTab === 'analytics' ? 'active' : ''}`} onClick={() => setActiveTab('analytics')}>Analytics</button>
        )}
        {isOwner && (
          <button className={`tab ${activeTab === 'staff' ? 'active' : ''}`} onClick={() => setActiveTab('staff')}>Staff</button>
        )}
      </div>

      {activeTab === 'details' && (
        <DetailsTab
          tournament={tournament}
          players={players}
          isOrganizer={isOrganizer}
          isAuthenticated={isAuthenticated}
          isRegistered={isRegistered}
          onRegister={handleRegister}
          onUpdate={reload}
        />
      )}

      {activeTab === 'events' && (
        <EventsTab
          tournament={tournament}
          isOrganizer={isOrganizer}
          onPlayersChange={reload}
        />
      )}

      {activeTab === 'players' && (
        <PlayersTab
          tournament={tournament}
          players={players}
          isOrganizer={isOrganizer}
          onPlayersChange={reload}
        />
      )}

      {activeTab === 'draw' && (
        <DrawTab
          tournament={tournament}
        />
      )}

      {activeTab === 'analytics' && isOrganizer && (
        <AnalyticsTab analytics={analytics} analyticsLoading={analyticsLoading} />
      )}

      {activeTab === 'schedule' && (
        <ScheduleTab
          tournament={tournament}
          matches={matches}
          isOrganizer={isOrganizer}
          canScore={canScore}
          getRoundName={getRoundName}
          onOpenScheduleModal={() => setShowScheduleModal(true)}
          onDeleteSchedule={handleDeleteSchedule}
          onViewDeleted={handleViewDeletedSchedule}
          onSelectWinner={handleUpdateMatch}
          onOpenScoreModal={handleOpenScoreModal}
          filterEventId={scheduleEventFilter}
          onFilterChange={setScheduleEventFilter}
        />
      )}

      {activeTab === 'staff' && isOwner && (
        <StaffTab tournament={tournament} isOwner={isOwner} />
      )}

      {/* Reopen Registration Modal */}
      {showReopenModal && (
        <div className="modal-overlay" onClick={() => setShowReopenModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Reopen Registration</h3>
            <div className="form-group">
              <label className="form-label">Remarks *</label>
              <textarea className="form-input" value={reopenRemarks} onChange={(e) => setReopenRemarks(e.target.value)} rows={4} required />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => setShowReopenModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleReopenRegistration} disabled={reopening}>Reopen</button>
            </div>
          </div>
        </div>
      )}

      {/* Registration History Modal */}
      {showHistoryModal && (
        <div className="modal-overlay" onClick={() => setShowHistoryModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <h3>Registration History</h3>
            {registrationHistory.length === 0 ? <p>No history found.</p> : (
              <div className="table-container" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table className="table">
                  <thead><tr><th>Action</th><th>From</th><th>To</th><th>By</th><th>Date</th></tr></thead>
                  <tbody>
                    {registrationHistory.map(item => (
                      <tr key={item.id}>
                        <td><span className="badge badge-primary">{item.action}</span></td>
                        <td>{item.previous_status || '-'}</td>
                        <td>{item.new_status || '-'}</td>
                        <td>{item.changed_by_name || 'System'}</td>
                        <td>{new Date(item.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {showScheduleModal && (
        <ScheduleModal
          tournament={tournament}
          matches={matches}
          onClose={() => setShowScheduleModal(false)}
          onSaved={() => { setShowScheduleModal(false); reload(); alert('Schedule generated!'); }}
        />
      )}

      {/* Score Modal */}
      {showScoreModal && selectedMatch && (
        <ScoreModal
          match={selectedMatch}
          sport={tournament?.sport_type}
          getRoundName={getRoundName}
          onClose={() => { setShowScoreModal(false); setSelectedMatch(null); }}
          onSaved={() => { setShowScoreModal(false); setSelectedMatch(null); reload(); }}
        />
      )}

      {/* Deleted Schedule Modal */}
      {showDeletedScheduleModal && (
        <div className="modal-overlay" onClick={() => setShowDeletedScheduleModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <h3>Deleted Schedule</h3>
            {deletedSchedule.length === 0 ? <p>No deleted schedules found.</p> : (
              <div className="table-container" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table className="table">
                  <thead><tr><th>Match #</th><th>Round</th><th>Player 1</th><th>Player 2</th><th>Scheduled Time</th></tr></thead>
                  <tbody>
                    {deletedSchedule.map(match => (
                      <tr key={match.id}>
                        <td>{match.match_number}</td>
                        <td>{getRoundName(match.round_number, match.event_id)}</td>
                        <td>{match.player1_name || 'TBD'}</td>
                        <td>{match.player2_name || 'TBD'}</td>
                        <td>{match.scheduled_time ? new Date(match.scheduled_time).toLocaleString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button className="btn btn-outline" onClick={() => setShowDeletedScheduleModal(false)}>Close</button>
              {deletedSchedule.length > 0 && (
                <button className="btn btn-danger" onClick={handlePermanentDeleteSchedule} disabled={deletingSchedule}>
                  {deletingSchedule ? 'Deleting...' : 'Permanently Delete All'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Schedule Confirm */}
      {showDeleteScheduleConfirm && (() => {
        // Events that actually have scheduled matches
        const scheduledEventIds = new Set(matches.filter(m => m.scheduled_time && m.event_id).map(m => m.event_id));
        const eventsWithSchedule = clearScheduleEvents.filter(ev => scheduledEventIds.has(ev.id));
        const multiEvent = eventsWithSchedule.length > 1;
        const selectedIds = clearScheduleEventIds; // null = all

        const toggleClearEvent = (evId) => {
          setClearScheduleEventIds(prev => {
            const current = prev || eventsWithSchedule.map(e => e.id);
            return current.includes(evId) ? current.filter(id => id !== evId) : [...current, evId];
          });
        };

        const clearLabel = !multiEvent || !selectedIds || selectedIds.length === eventsWithSchedule.length
          ? 'Clear All'
          : `Clear ${selectedIds.length} Event${selectedIds.length !== 1 ? 's' : ''}`;

        return (
          <div className="modal-overlay" onClick={() => setShowDeleteScheduleConfirm(false)}>
            <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
              <h3>Clear Schedule Timings?</h3>
              <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                This clears scheduled times and court assignments. The match draw remains intact.
              </p>

              {multiEvent && (
                <div style={{ margin: '1rem 0', padding: '0.75rem', background: '#F0FDFA', borderRadius: '4px', border: '1px solid #99F6E4' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>Select events to clear:</span>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      <button type="button" style={{ fontSize: '0.7rem', color: '#0D9488', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        onClick={() => setClearScheduleEventIds(null)}>All</button>
                      <span style={{ color: '#d1d5db' }}>|</span>
                      <button type="button" style={{ fontSize: '0.7rem', color: '#0D9488', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        onClick={() => setClearScheduleEventIds([])}>None</button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                    {eventsWithSchedule.map(ev => {
                      const checked = !selectedIds || selectedIds.includes(ev.id);
                      return (
                        <label key={ev.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', padding: '4px 0' }}>
                          <input type="checkbox" checked={checked} onChange={() => toggleClearEvent(ev.id)} />
                          <span>{ev.name}</span>
                          {ev.event_type && <span style={{ fontSize: '0.7rem', padding: '1px 5px', background: '#CCFBF1', color: '#115E59', borderRadius: 999 }}>{ev.event_type}</span>}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button className="btn btn-outline" onClick={() => setShowDeleteScheduleConfirm(false)} disabled={deletingSchedule}>Cancel</button>
                <button
                  className="btn btn-danger"
                  onClick={confirmDeleteSchedule}
                  disabled={deletingSchedule || (multiEvent && selectedIds !== null && selectedIds.length === 0)}
                >
                  {deletingSchedule ? 'Clearing...' : clearLabel}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Toast notification */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '40px', left: '50%', transform: 'translateX(-50%)',
          backgroundColor: '#2d3436', color: '#fff', padding: '14px 28px', borderRadius: '8px',
          zIndex: 99999, boxShadow: '0 10px 25px rgba(0,0,0,0.4)', display: 'flex',
          alignItems: 'center', gap: '12px', fontSize: '0.95rem', fontWeight: '500',
          border: '1px solid rgba(255,255,255,0.1)', animation: 'slideUp 0.3s ease-out'
        }}>
          <span style={{ color: '#00b894', fontSize: '1.2rem' }}>✓</span> {toast}
        </div>
      )}
    </div>
  );
};

export default TournamentDetail;
